import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Focus, Users, Car, Globe, Languages, Bot, 
  CheckCircle2, Target, PenTool, Code2, Rocket, Headset, BrainCircuit
} from 'lucide-react';

import { 
  AutomotiveIcon, ChemicalIcon, PowerEnergyIcon, ManufacturingIcon, 
  ITIcon, HealthcareIcon, EducationIcon, GovtIcon 
} from '../components/IndustryIcons';

import opMonImg from '../assets/solutions/operation_monitoring.png';
import headCountImg from '../assets/solutions/head_count.png';
import vehiclePlateImg from '../assets/solutions/vehicle_plate.png';
import langTransImg from '../assets/solutions/language_translation.png';
import engMarImg from '../assets/solutions/english_marathi.png';
import chatbotImg from '../assets/solutions/industry_chatbots.png';

const SolutionsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full bg-[#f9f8f4] min-h-screen text-brand-textDark font-sans">
      
      {/* SECTION 1: Our Solutions Grid */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-brand-gold font-bold tracking-widest uppercase text-sm mb-4">OUR SOLUTIONS</p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6">AI-Powered Solutions for Real-World Impact</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            From Computer Vision to Language AI - we build smart, scalable and industry-ready solutions.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Card 1: Operation Monitoring */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow flex flex-col h-full">
            <div className="bg-gray-100 rounded-2xl h-48 mb-6 overflow-hidden relative flex items-center justify-center">
               <img src={opMonImg} alt="Operation Monitoring" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-brand-gold/10 rounded-xl text-brand-gold shrink-0">
                <Focus className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a]">Operation<br/>Monitoring</h3>
            </div>
            <p className="text-gray-600 text-sm flex-1">
              AI-powered real-time monitoring of operations to improve productivity, safety and efficiency.
            </p>
            <Link to="/contact-us" className="text-brand-gold font-semibold text-sm flex items-center gap-1 mt-6 hover:gap-2 transition-all">
              Learn More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Card 2: Head Count */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow flex flex-col h-full">
            <div className="bg-gray-100 rounded-2xl h-48 mb-6 overflow-hidden relative flex items-center justify-center">
               <img src={headCountImg} alt="Head Count" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-brand-gold/10 rounded-xl text-brand-gold shrink-0">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a]">Head Count</h3>
            </div>
            <p className="text-gray-600 text-sm flex-1">
              Accurate people counting solutions for workplaces, events, industries and public spaces.
            </p>
            <Link to="/contact-us" className="text-brand-gold font-semibold text-sm flex items-center gap-1 mt-6 hover:gap-2 transition-all">
              Learn More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Card 3: Vehicle Number Plate */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow flex flex-col h-full">
            <div className="bg-gray-100 rounded-2xl h-48 mb-6 overflow-hidden relative flex items-center justify-center">
               <img src={vehiclePlateImg} alt="Vehicle Number Plate Detection" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-brand-gold/10 rounded-xl text-brand-gold shrink-0">
                <Car className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a]">Vehicle Number<br/>Plate Detection</h3>
            </div>
            <p className="text-gray-600 text-sm flex-1">
              Automatic vehicle number plate recognition (ANPR) with high accuracy in real-time.
            </p>
            <Link to="/contact-us" className="text-brand-gold font-semibold text-sm flex items-center gap-1 mt-6 hover:gap-2 transition-all">
              Learn More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Card 4: Language Translation */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow flex flex-col h-full">
            <div className="bg-white border border-gray-100 shadow-inner rounded-2xl h-48 mb-6 overflow-hidden relative flex flex-col items-center justify-center gap-4">
               <img src={langTransImg} alt="Language Translation" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-brand-gold/10 rounded-xl text-brand-gold shrink-0">
                <Globe className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a]">Language<br/>Translation</h3>
            </div>
            <p className="text-gray-600 text-sm flex-1">
              Real-time language translation for Google Meet and Microsoft Teams meetings.
            </p>
            <Link to="/contact-us" className="text-brand-gold font-semibold text-sm flex items-center gap-1 mt-6 hover:gap-2 transition-all">
              Learn More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Card 5: English to Marathi */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow flex flex-col h-full">
            <div className="bg-[#f9f8f4] rounded-2xl h-48 mb-6 overflow-hidden relative flex items-center justify-center">
               <img src={engMarImg} alt="English to Marathi Translation" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-brand-gold/10 rounded-xl text-brand-gold shrink-0">
                <Languages className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a]">English to<br/>Marathi Translation</h3>
            </div>
            <p className="text-gray-600 text-sm flex-1">
              Accurate and natural translations from English to Marathi for business and governance.
            </p>
            <Link to="/contact-us" className="text-brand-gold font-semibold text-sm flex items-center gap-1 mt-6 hover:gap-2 transition-all">
              Learn More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Card 6: Industry Chatbots */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow flex flex-col h-full">
            <div className="bg-[#1a1a1a] rounded-2xl h-48 mb-6 overflow-hidden relative flex flex-col items-center justify-center p-4">
               <img src={chatbotImg} alt="Industry Chatbots" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-brand-gold/10 rounded-xl text-brand-gold shrink-0">
                <Bot className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a]">Industry Chatbots</h3>
            </div>
            <p className="text-gray-600 text-sm flex-1">
              Intelligent chatbots for Automotive, Chemical Industries, MSEB and more.
            </p>
            <Link to="/contact-us" className="text-brand-gold font-semibold text-sm flex items-center gap-1 mt-6 hover:gap-2 transition-all">
              Learn More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 2: Industries We Serve */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#111111] text-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16 flex flex-col items-center">
            <p className="text-brand-gold font-bold tracking-widest uppercase text-sm mb-4">INDUSTRIES WE SERVE</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Empowering Industries with AI</h2>
            <div className="w-12 h-0.5 bg-brand-gold"></div>
          </div>

          <div className="flex flex-row justify-between items-center text-center overflow-x-auto pb-4 gap-4 md:gap-0">
            {[
              { icon: AutomotiveIcon, label: "Automotive\nIndustry" },
              { icon: ChemicalIcon, label: "Chemical\nIndustry" },
              { icon: PowerEnergyIcon, label: "Power & Energy\n(MSEB)" },
              { icon: ManufacturingIcon, label: "Manufacturing\nIndustry" },
              { icon: ITIcon, label: "IT & ITES" },
              { icon: HealthcareIcon, label: "Healthcare" },
              { icon: EducationIcon, label: "Education" },
              { icon: GovtIcon, label: "Govt. & Public\nSector" }
            ].map((ind, i, arr) => (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center group cursor-pointer px-2 sm:px-4 shrink-0 min-w-[120px] md:min-w-0">
                  <div className="text-brand-gold mb-4 transform group-hover:scale-110 transition-transform duration-300">
                    <ind.icon className="w-10 h-10 md:w-12 md:h-12 mx-auto" />
                  </div>
                  <p className="text-xs md:text-sm text-gray-300 group-hover:text-white font-medium whitespace-pre-line leading-snug transition-colors duration-300">{ind.label}</p>
                </div>
                {i < arr.length - 1 && (
                  <div className="h-16 w-px bg-white/10 hidden md:block shrink-0"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: Why Choose & Development Process */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          {/* Left: Why Choose */}
          <div>
            <p className="text-brand-gold font-bold tracking-widest uppercase text-xs mb-3">WHY CHOOSE AIGENIUS?</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-8">Your Partner in AI Innovation</h2>
            
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <ul className="space-y-4 flex-1">
                {[
                  "Expert AI/ML Team with Industry Experience",
                  "Custom-Built Solutions for Real Business Problems",
                  "Cutting-Edge Technology & Best Practices",
                  "Scalable, Secure & Reliable Solutions",
                  "End-to-End Development & Support"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              
              {/* Brain Graphic Placeholder */}
              <div className="w-48 h-48 relative shrink-0 hidden md:block">
                 <div className="absolute bottom-0 w-full h-8 rounded-[100%] bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-50 blur-[5px]" />
                 <div className="absolute inset-0 flex items-center justify-center pb-4">
                    <BrainCircuit className="w-24 h-24 text-brand-gold/80" strokeWidth={1} />
                 </div>
              </div>
            </div>
          </div>

          {/* Right: Development Process */}
          <div>
            <p className="text-brand-gold font-bold tracking-widest uppercase text-xs mb-3">OUR DEVELOPMENT PROCESS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-8">From Idea to Intelligent Solution</h2>
            
            <div className="flex flex-row justify-between items-start pt-4 relative">
              {/* Connecting dashed line */}
              <div className="absolute top-[32px] left-8 right-8 h-[2px] border-t-2 border-dashed border-brand-gold/30 -z-10 hidden sm:block" />
              
              {[
                { icon: Target, step: "1", title: "1. Understand", desc: "We analyze your needs and challenges." },
                { icon: PenTool, step: "2", title: "2. Design", desc: "We design the best AI solution tailored to your goals." },
                { icon: Code2, step: "3", title: "3. Develop", desc: "We build, train and test for accuracy and reliability." },
                { icon: Rocket, step: "4", title: "4. Deploy", desc: "We deploy and integrate seamlessly into your system." },
                { icon: Headset, step: "5", title: "5. Support", desc: "We provide continued support and improvements." }
              ].map((step, i) => (
                <div key={i} className="flex flex-col items-center text-center w-1/5 px-1 sm:px-2 relative group bg-white">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[#f9f8f4] border-2 border-brand-gold/20 flex items-center justify-center mb-4 text-brand-gold group-hover:bg-brand-gold group-hover:text-[#1a1a1a] transition-colors relative z-10 shadow-sm">
                    <step.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <h4 className="font-bold text-[#1a1a1a] text-[10px] sm:text-sm mb-2">{step.title}</h4>
                  <p className="text-[10px] sm:text-[11px] text-gray-500 leading-tight hidden sm:block">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
          
        </div>
      </section>

      {/* SECTION 4: CTA */}
      <section className="pb-20 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-6xl mx-auto bg-[#1A1A1A] rounded-3xl p-8 md:p-14 flex flex-col md:flex-row items-center justify-between relative overflow-hidden shadow-2xl">
          {/* BG Element */}
          <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-brand-gold/10 rounded-full blur-[60px]" />
          
          <div className="flex items-center gap-4 md:gap-6 relative z-10 mb-8 md:mb-0">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl border border-brand-gold/30 flex items-center justify-center shrink-0 bg-brand-gold/5">
               <Users className="w-6 h-6 md:w-8 md:h-8 text-brand-gold" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Let's Build the Future Together</h2>
              <p className="text-sm md:text-base text-gray-400">Have an idea or a challenge? Let our AI experts turn it into an intelligent solution.</p>
            </div>
          </div>
          
          <Link to="/contact-us" className="relative z-10 shrink-0 bg-brand-gold hover:bg-brand-goldHover text-[#1a1a1a] font-bold py-3 px-6 md:py-4 md:px-8 rounded-xl transition-all flex items-center gap-2 shadow-[0_0_15px_rgba(255,184,28,0.3)]">
            Get in Touch <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
      
    </div>
  );
};

export default SolutionsPage;
